import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import { getAppInstanceId } from "@models/multiuser/appInstanceId";
import { loadCloudConfig } from "@models/storage/cloudConfig";
import type { ITelemetryService } from "@models/telemetry/ITelemetryService";
import { buildTelemetryPropsJson } from "@models/telemetry/TelemetryEventPropsBuilder";
import type {
  ConnectionState,
  IEventModel,
  ServerEvent,
} from "./IEventModel";

const RECONNECT_DELAY_MS = 2_000;
const SESSION_ROUTE = "sessionevents";
const SESSION_AUTH_SUBPROTOCOL = "campfire-session-v1";
const MAX_QUEUED_EVENTS = 100;
const DEBUG_EVENTS = import.meta.env.DEV
  || import.meta.env.VITE_CAMPFIRE_EVENTS_DEBUG === "true"
  || window.localStorage.getItem("campfire.events.debug") === "true";

/**
 * Numeric wire type codes (`tc`) for outgoing session events.
 *
 * Unity's `tc` is the array index of the payload class in
 * `SessionModel.m_typeNames` (`Models/EventModel/SessionModel.cs`), bound to
 * the event string by which class each `AWSServerEventSenderModel.Send*`
 * method news up. The code is authoritative for DESERIALIZATION on Unity
 * receivers (`m_codeToPayloadType[message.tc]`) — a wrong-but-valid code
 * silently strips every subclass field (ProjectId, SceneId, EntityId, …), so
 * every type the web publishes MUST be listed here with its Unity value.
 *
 * (Unity's own heartbeat is stamped 53 due to `BaseEventData` being
 * registered twice; 6 also maps to `BaseEventData` on receive, so 6 stays
 * receive-compatible.)
 */
const EVENT_TYPE_CODES: Readonly<Record<string, number>> = {
  active_tool: 0, // ActiveToolEventData
  launch_application: 4, // ApplicationEventData
  quit_application: 4, // ApplicationEventData
  ask_to_follow: 5, // AsktoFollowEventData
  heartbeat: 6, // BaseEventData
  follower_change: 17, // FollowChangeEventData (wire type is "follower_change")
  create_entity: 13, // EntityEventData
  delete_entity: 13, // EntityEventData
  update_entitysnapshot: 14, // EntitySnapshotEventData
  grabtool_operation: 18, // GrabtoolEventData
  // grabtool_properties (19) intentionally absent: Unity's own sender stamps
  // it with the flashlight_properties type (copy-paste bug,
  // AWSServerEventSenderModel.cs:845-851), so nothing real ever sends tc 19.
  load_project: 21, // LoadProjectEventData
  update_material: 23, // MaterialEventData
  open_project: 24, // OpenProjectEventData
  participant_transform: 25, // ParticipantTransformEventData
  presenter_change: 26, // PresenterChangeEventData
  create_project: 27, // ProjectEventData
  update_project: 27, // ProjectEventData
  delete_project: 27, // ProjectEventData
  close_project: 27, // ProjectEventData
  unload_project: 27, // ProjectEventData
  reserve_color: 29, // ReserveColorEventData
  create_scene: 31, // SceneEventData
  delete_scene: 31, // SceneEventData
  update_scene: 31, // SceneEventData
  update_scene_tr: 32, // SceneTransformEventData
  shared_state_snapshot: 34, // SharedStateEventData
  create_annotation: 2, // AnnotationEventData
  edit_annotation: 2, // AnnotationEventData (typ string picks the op, not the tc)
  delete_annotation: 10, // DeleteAnnotationEventData
  sketch_uploaded: 36, // SketchUploadedEventData
  evict_user: 47, // EvictUserEventData
  change_sharing: 48, // ChangeSharingEventData
  load_scene: 39, // TargetSceneEventData
  unload_scene: 39, // TargetSceneEventData
  update_thumbnail: 39, // TargetSceneEventData
  bulk_update_entitysnapshot: 55, // BulkEntitySnapshotEventData
  update_selectionset: 56, // SelectionSetEventData
  // FolderEventData. All FOUR folder operations ride this one type; the
  // payload's `folderEvent` ordinal picks create / delete / rename / membership
  // (`AWSServerEventSenderModel.cs:257-308`). There is no `create_folder` or
  // `delete_folder` wire string to add.
  update_folder: 57,
  update_mediatile: 58, // UpdateMediaTileEventData
  update_mediaasset: 59, // UpdateMediaAssetEventData
  create_mediatile: 60, // CreateMediaTileEventData
  delete_mediatile: 61, // DeleteMediaTileEventData
};

const warnedUnknownTypeCodes = new Set<string>();

/**
 * Browser WebSocket transport for project/session events.
 *
 * The endpoint can be supplied with `VITE_CAMPFIRE_EVENTS_WS_URL` as an absolute
 * `ws://`, `wss://`, `http://`, or `https://` URL. If it is absent, the app
 * uses Unity's `aws_sessions_url` from app-config (`campssvc.../v1`). Browsers
 * cannot set an Authorization upgrade header, so the Cognito ID token rides the
 * WebSocket subprotocol list.
 */
export class WebSocketEventModel implements IEventModel {
  readonly onEvent = new Emitter<ServerEvent>();
  readonly onStateChanged = new Emitter<ConnectionState>();

  private m_state: ConnectionState = "disconnected";
  private m_socket: WebSocket | null = null;
  private m_reconnectTimer: number | null = null;
  private m_shouldReconnect = false;
  private m_sequenceNumber = 0;
  private m_connectPromise: Promise<void> | null = null;
  private m_sessionUserId: string | null = null;
  private readonly m_sendQueue: ServerEvent[] = [];

  constructor(
    private readonly m_telemetry?: ITelemetryService,
    private readonly m_auth: IAuthModel = Ioc.resolve<IAuthModel>(AUTH_MODEL),
  ) {}

  get state(): ConnectionState {
    return this.m_state;
  }

  async connectAsync(signal?: AbortSignal): Promise<void> {
    this.m_shouldReconnect = true;
    if (this.m_socket?.readyState === WebSocket.OPEN) {
      debug("connectAsync: already open");
      this.setState("connected");
      this.flushQueue();
      return;
    }
    if (this.m_connectPromise) {
      debug("connectAsync: reusing in-flight connect");
      return this.m_connectPromise;
    }
    this.clearReconnectTimer();
    this.setState(this.m_state === "connected" ? "connected" : "connecting");

    const connection = await this.buildConnection();
    if (signal?.aborted) return;
    debug("connectAsync: opening socket", scrubUrl(connection.url));

    this.m_connectPromise = new Promise<void>((resolve) => {
      const socket = connection.protocols
        ? new WebSocket(connection.url, connection.protocols)
        : new WebSocket(connection.url);
      socket.binaryType = "arraybuffer";
      this.m_socket = socket;

      socket.onopen = () => {
        debug("socket open", scrubUrl(connection.url));
        this.m_sessionUserId = connection.userId;
        this.setState("connected");
        this.flushQueue();
        resolve();
      };
      socket.onmessage = (event) => {
        void this.handleMessage(event.data);
      };
      socket.onerror = () => {
        console.warn("[WebSocketEventModel] socket error while connecting to session service");
        debug("socket error", scrubUrl(connection.url));
        if (this.m_state === "connecting") {
          this.m_connectPromise = null;
          resolve();
        }
      };
      socket.onclose = (event) => {
        if (event.code !== 1000) {
          console.warn("[WebSocketEventModel] socket closed", {
            code: event.code,
            reason: event.reason,
            clean: event.wasClean,
          });
        }
        debug("socket close", { code: event.code, reason: event.reason, clean: event.wasClean });
        if (this.m_socket === socket) this.m_socket = null;
        this.m_connectPromise = null;
        if (this.m_state === "connecting") resolve();
        if (this.m_shouldReconnect) this.scheduleReconnect();
        else this.setState("disconnected");
      };

      signal?.addEventListener(
        "abort",
        () => {
          socket.close();
          resolve();
        },
        { once: true },
      );
    });
    await this.m_connectPromise;
  }

  disconnect(): void {
    this.m_shouldReconnect = false;
    this.clearReconnectTimer();
    this.m_socket?.close();
    this.m_socket = null;
    this.m_sessionUserId = null;
    this.setState("disconnected");
  }

  send(envelope: ServerEvent): void {
    const normalized: ServerEvent = {
      ...envelope,
      eventUid: envelope.eventUid ?? randomUuid(),
      senderTimestampMillis: envelope.senderTimestampMillis ?? Date.now(),
    };
    this.capture(normalized);

    const socket = this.m_socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      debug("send queued: socket not open", {
        type: normalized.type,
        readyState: socket?.readyState ?? "none",
      });
      this.enqueue(normalized);
      return;
    }
    this.sendNow(normalized);
  }

  private async buildConnection(): Promise<{ url: string; protocols?: string[]; userId: string | null }> {
    const explicit = import.meta.env.VITE_CAMPFIRE_EVENTS_WS_URL as string | undefined;
    const token = await this.m_auth.getIdToken();
    const userId = sessionUserIdFromIdToken(token)
      ?? nonEmptyString(this.m_auth.currentUser?.userId)
      ?? null;
    const base = explicit?.trim() || defaultSessionEndpoint();
    const url = toWebSocketUrl(base);
    debug("buildUrl", {
      base: scrubUrl(url.toString()),
      explicit: Boolean(explicit?.trim()),
      hasToken: Boolean(token),
      hasUserId: Boolean(userId),
      authMode: !token ? "none" : "subprotocol",
      defaultEndpoint: !explicit?.trim(),
    });
    if (token) {
      return {
        url: url.toString(),
        protocols: [SESSION_AUTH_SUBPROTOCOL, `authorization.${token}`],
        userId,
      };
    }
    return { url: url.toString(), userId };
  }

  private async handleMessage(raw: unknown): Promise<void> {
    const text = await decodeFrame(raw);
    if (!text) {
      debug("received unsupported frame", frameKind(raw));
      return;
    }
    debug("received raw frame", { kind: frameKind(raw), bytes: text.length, preview: preview(text) });
    try {
      const parsed = JSON.parse(text) as {
        type?: unknown;
        route?: unknown;
        typ?: unknown;
        payload?: unknown;
        eventUid?: unknown;
        uid?: unknown;
        id?: unknown;
        tc?: unknown;
      };
      const sessionPayload = parseSessionPayload(parsed.payload);
      if (sessionPayload) {
        const type = stringField(sessionPayload, "typ", "type", "route");
        if (!type) return;
        debug("received session event", {
          type,
          tc: parsed.tc,
          keys: Object.keys(sessionPayload).sort(),
          summary: summarizePayload(sessionPayload),
        });
        this.onEvent.emit({
          type,
          payload: sessionPayload,
          eventUid: stringField(sessionPayload, "id", "eventUid") ?? undefined,
        });
        return;
      }

      const type = typeof parsed.type === "string"
        ? parsed.type
        : typeof parsed.route === "string"
          ? parsed.route
          : typeof parsed.typ === "string"
            ? parsed.typ
            : "";
      if (!type) return;
      debug("received event", {
        type,
        keys: Object.keys(parsed).sort(),
      });
      this.onEvent.emit({
        type,
        payload: parsed.payload ?? parsed,
        eventUid: typeof parsed.eventUid === "string"
          ? parsed.eventUid
          : typeof parsed.id === "string"
            ? parsed.id
            : undefined,
      });
    } catch (err) {
      console.warn("[WebSocketEventModel] failed to parse event frame", {
        error: err,
        preview: preview(text),
      });
    }
  }

  private scheduleReconnect(): void {
    debug("scheduleReconnect");
    this.setState(this.m_state === "connected" ? "reconnecting" : "connecting");
    this.clearReconnectTimer();
    this.m_reconnectTimer = window.setTimeout(() => {
      void this.connectAsync();
    }, RECONNECT_DELAY_MS);
  }

  private clearReconnectTimer(): void {
    if (this.m_reconnectTimer === null) return;
    window.clearTimeout(this.m_reconnectTimer);
    this.m_reconnectTimer = null;
  }

  private setState(next: ConnectionState): void {
    if (this.m_state === next) return;
    debug("state", `${this.m_state} -> ${next}`);
    this.m_state = next;
    this.onStateChanged.emit(next);
  }

  private toSessionMessage(envelope: ServerEvent): Record<string, unknown> | null {
    const rawPayload = envelope.payload && typeof envelope.payload === "object"
      ? envelope.payload as Record<string, unknown>
      : { value: envelope.payload };
    const payload = wirePayloadFor(envelope.type, rawPayload);
    const type = envelope.type;
    const eventUid = envelope.eventUid ?? randomUuid();
    const userId = eventUserId(
      payload,
      this.m_sessionUserId ?? this.m_auth.currentUser?.userId,
    );
    if (!userId) {
      // Unity's AWSServerEventSenderModel stamps OriginatingUserId on every
      // outgoing event from the authenticated current user. Sending without it
      // can make the session service overwrite a participant row with no
      // user_id, so fail closed at the shared transport boundary.
      console.warn(`[WebSocketEventModel] refusing to send "${type}" without a user id`);
      return null;
    }
    const eventPayload = {
      ...payload,
      typ: type,
      id: eventUid,
      status: 1,
      ts: envelope.senderTimestampMillis ?? Date.now(),
      uid: userId,
      OriginatingUserId: userId,
      user_id: userId,
      aid: eventAppInstanceId(payload),
    };
    this.m_sequenceNumber += 1;
    const typeCode = EVENT_TYPE_CODES[type];
    if (typeCode === undefined && !warnedUnknownTypeCodes.has(type)) {
      // A wrong tc makes Unity receivers deserialize the payload as a bare
      // BaseEventData, silently dropping every subclass field — add the type
      // to EVENT_TYPE_CODES (from Unity's SessionModel.m_typeNames index).
      warnedUnknownTypeCodes.add(type);
      console.warn(`[WebSocketEventModel] no wire type code for "${type}" — sending tc=6 (BaseEventData); Unity peers will strip its payload fields`);
    }
    const message = {
      route: SESSION_ROUTE,
      payload: JSON.stringify(eventPayload),
      tc: typeCode ?? 6,
      sn: this.m_sequenceNumber,
    };
    debug("toSessionMessage", {
      type,
      tc: message.tc,
      sn: message.sn,
      payload: summarizePayload(eventPayload),
    });
    return message;
  }

  private enqueue(envelope: ServerEvent): void {
    // Continuous-state streams are worthless stale: replaying queued poses
    // on reconnect serves peers a burst of outdated frames, and at their
    // 50 Hz publish rate they would evict the ring's one-shot events
    // (presenter_change, follower_change, …) within ~2 s of camera motion.
    // Drop them — the next live sample supersedes, and heartbeat +
    // open_project re-announce restore presence after reconnect.
    if (envelope.type === "participant_transform") {
      debug("dropped while disconnected", envelope.type);
      return;
    }
    this.m_sendQueue.push(envelope);
    if (this.m_sendQueue.length > MAX_QUEUED_EVENTS) this.m_sendQueue.shift();
    debug("queued", envelope.type);
  }

  private flushQueue(): void {
    const pending = this.m_sendQueue.splice(0);
    for (const envelope of pending) this.sendNow(envelope);
  }

  private sendNow(envelope: ServerEvent): void {
    const socket = this.m_socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      this.enqueue(envelope);
      return;
    }
    const message = this.toSessionMessage(envelope);
    if (!message) return;
    debug("send", {
      type: envelope.type,
      tc: message.tc,
      sn: message.sn,
      bytes: JSON.stringify(message).length,
    });
    socket.send(new TextEncoder().encode(JSON.stringify(message)));
  }

  private capture(envelope: ServerEvent): void {
    const user = this.m_auth.currentUser;
    const customerId = user?.customerId ?? "";
    if (!user || !customerId || !envelope.eventUid || !envelope.senderTimestampMillis) return;

    this.m_telemetry?.capture({
      customerId,
      source: "eventbus",
      eventType: envelope.type,
      eventUid: envelope.eventUid,
      originatingUserId: user.userId,
      timestampMillis: envelope.senderTimestampMillis,
      propsJson: buildTelemetryPropsJson(envelope.type, envelope.payload),
    });
  }
}

/**
 * Unity marks these fields `[NonSerialized]`: they enrich the durable
 * telemetry row but must not change the realtime session wire contract.
 */
function wirePayloadFor(
  eventType: string,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  if (
    eventType !== "launch_application"
    && eventType !== "quit_application"
    && eventType !== "close_project"
    && eventType !== "unload_scene"
  ) {
    return payload;
  }

  const wire = { ...payload };
  delete wire.DeviceType;
  delete wire.deviceType;
  delete wire.Duration;
  delete wire.duration;
  delete wire.IsComplete;
  delete wire.isComplete;
  return wire;
}

function eventUserId(payload: Record<string, unknown>, fallback: string | undefined): string | undefined {
  return nonEmptyString(payload.uid)
    ?? nonEmptyString(payload.OriginatingUserId)
    ?? nonEmptyString(payload.user_id)
    ?? nonEmptyString(payload.UserId)
    ?? nonEmptyString(fallback);
}

function eventAppInstanceId(payload: Record<string, unknown>): string {
  return nonEmptyString(payload.aid)
    ?? nonEmptyString(payload.OriginatingAppInstanceId)
    ?? getAppInstanceId();
}

function sessionUserIdFromIdToken(token: string | null): string | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as Record<string, unknown>;
    // Unity's `CognitoIdp.GetUserInfo` uses `cognito:username` for
    // `UserInfo.UserID`, which `AWSServerEventSenderModel` stamps as `uid` on
    // every session event. In the current Cognito pool it matches `sub`; keep
    // `sub` only as the same fallback Unity's auth parsing effectively has
    // when the username claim is absent.
    return nonEmptyString(payload["cognito:username"])
      ?? nonEmptyString(payload.sub)
      ?? null;
  } catch {
    return null;
  }
}

function base64UrlDecode(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const binary = window.atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Resolve a configured session endpoint into an absolute WebSocket URL.
 *
 * `VITE_CAMPFIRE_EVENTS_WS_URL` must be absolute. Relative URLs would point the
 * browser at this app's origin and re-create the session proxy path this client
 * no longer uses.
 *
 * `https:` -> `wss:` and `http:` -> `ws:`; every one of those is a "special"
 * scheme in the URL Standard, so the protocol setter accepts the swap. An input
 * that is already `ws:`/`wss:` passes through untouched.
 */
function toWebSocketUrl(base: string): URL {
  if (!/^(wss?|https?):\/\//i.test(base)) {
    throw new Error(`VITE_CAMPFIRE_EVENTS_WS_URL must be an absolute websocket URL; got ${JSON.stringify(base)}.`);
  }
  const url = new URL(base);
  if (url.protocol === "https:") url.protocol = "wss:";
  else if (url.protocol === "http:") url.protocol = "ws:";
  return url;
}

function defaultSessionEndpoint(): string {
  return loadCloudConfig().sessionsEndpoint;
}

async function decodeFrame(raw: unknown): Promise<string | null> {
  if (typeof raw === "string") return raw;
  if (raw instanceof ArrayBuffer) return new TextDecoder().decode(raw);
  if (ArrayBuffer.isView(raw)) {
    return new TextDecoder().decode(raw);
  }
  if (raw instanceof Blob) return raw.text();
  return null;
}

function parseSessionPayload(payload: unknown): Record<string, unknown> | null {
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      const record = asRecord(parsed);
      return record && stringField(record, "typ", "type", "route") ? record : null;
    } catch {
      return null;
    }
  }
  const record = asRecord(payload);
  return record && stringField(record, "typ", "type", "route") ? record : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function stringField(
  value: Record<string, unknown>,
  ...keys: ReadonlyArray<string>
): string | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "string" && field.length > 0) return field;
  }
  return null;
}

function scrubUrl(value: string): string {
  const url = new URL(value);
  if (url.searchParams.has("authorization")) url.searchParams.set("authorization", "<redacted>");
  return url.toString();
}

function frameKind(raw: unknown): string {
  if (typeof raw === "string") return "string";
  if (raw instanceof ArrayBuffer) return "arraybuffer";
  if (ArrayBuffer.isView(raw)) return raw.constructor.name;
  if (raw instanceof Blob) return "blob";
  return typeof raw;
}

function preview(value: string): string {
  return value.length > 700 ? `${value.slice(0, 700)}...` : value;
}

function summarizePayload(value: Record<string, unknown>): Record<string, unknown> {
  return {
    typ: value.typ ?? value.type,
    id: value.id ?? value.eventUid,
    uid: value.uid ?? value.OriginatingUserId,
    aid: value.aid ?? value.OriginatingAppInstanceId,
    projectId: value.projectId ?? value.project_id ?? value.ProjectId,
    customerId: value.customerId ?? value.CustomerId ?? value.act_id,
    name: value.name ?? value.userTag ?? value.DisplayName,
    initials: value.initials ?? value.Initials,
    activeUsers: Array.isArray(value.activeUsers)
      ? value.activeUsers.length
      : Array.isArray(value.ActiveUsers)
        ? value.ActiveUsers.length
        : undefined,
  };
}

function debug(...args: ReadonlyArray<unknown>): void {
  if (!DEBUG_EVENTS) return;
  console.debug("[WebSocketEventModel]", ...args);
}

